/**
 * ProductDetail — public product page at /shop/p/:slug
 *
 * Features:
 * - Multi-image gallery (main image + clickable thumbnails)
 * - Variation selector (required for products with variations)
 * - Per-variation inventory status (In Stock / Low Stock / Out of Stock)
 * - Add-to-cart button disabled when no variation chosen OR chosen variation is out of stock
 * - Falls back gracefully for products with no variations (legacy single-SKU items)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Minus,
  ShoppingBag,
  Check,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";
import { useCart } from "../lib/cart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const stockState = (inventory, lowThreshold) => {
  const n = Number(inventory || 0);
  if (n <= 0) return { kind: "out", label: "Out of Stock" };
  if (n <= Number(lowThreshold || 5))
    return { kind: "low", label: `Low Stock — only ${n} left` };
  return { kind: "in", label: "In Stock" };
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, openCart, getCartQty } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getShopProduct(slug);
        if (cancelled) return;
        setProduct(data);
        // Auto-select first active, in-stock variation if any
        const variations = (data.variations || []).filter((v) => v.active !== false);
        const firstInStock = variations.find((v) => Number(v.inventory) > 0);
        setSelectedVariationId((firstInStock || variations[0])?.id || null);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          logger.warn("ProductDetail fetch failed:", err.message);
          toast.error("Couldn't load this product");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const images = useMemo(() => {
    if (!product) return [];
    if (product.images && product.images.length) return product.images;
    if (product.thumbnail_url) return [product.thumbnail_url];
    return [];
  }, [product]);

  const variations = useMemo(
    () => (product?.variations || []).filter((v) => v.active !== false).sort(
      (a, b) => (a.display_order || 0) - (b.display_order || 0)
    ),
    [product]
  );

  const selectedVariation = useMemo(
    () => variations.find((v) => v.id === selectedVariationId) || null,
    [variations, selectedVariationId]
  );

  const effectivePrice = useMemo(() => {
    if (!product) return 0;
    if (selectedVariation && selectedVariation.price != null) return Number(selectedVariation.price);
    if (product.price != null) return Number(product.price);
    return 0;
  }, [product, selectedVariation]);

  // Max purchasable qty = variation inventory (if variations) OR a soft cap of 99.
  // If item is already in the cart, subtract the existing cart line so total never exceeds stock.
  const availableStock = useMemo(() => {
    if (!product) return 0;
    if (selectedVariation) return Math.max(0, Number(selectedVariation.inventory || 0));
    if (variations.length > 0) return 0; // no variation chosen yet
    return 99; // legacy single-SKU (no inventory tracking)
  }, [product, selectedVariation, variations.length]);

  const inCartQty = useMemo(() => {
    if (!product) return 0;
    return getCartQty(product.id, selectedVariation?.id || null);
  }, [product, selectedVariation, getCartQty]);

  const remainingAllowable = Math.max(0, availableStock - inCartQty);

  // Clamp qty whenever the ceiling changes (variation switched, item added elsewhere, etc.)
  useEffect(() => {
    if (remainingAllowable <= 0) {
      setQuantity(1);
    } else if (quantity > remainingAllowable) {
      setQuantity(remainingAllowable);
    }
  }, [remainingAllowable, quantity]);

  const handleQuantityChange = (raw) => {
    const n = parseInt(String(raw), 10);
    if (Number.isNaN(n)) {
      setQuantity(1);
      return;
    }
    if (n < 1) {
      setQuantity(1);
      return;
    }
    if (n > remainingAllowable) {
      // Cap silently on stepper / on-the-fly typing; toast only fires on explicit Add.
      setQuantity(Math.max(1, remainingAllowable));
      return;
    }
    setQuantity(n);
  };

  const handleAddToCart = () => {
    if (!product) return;
    if (variations.length > 0 && !selectedVariation) {
      toast.error("Please choose an option first");
      return;
    }
    if (selectedVariation && Number(selectedVariation.inventory) <= 0) {
      toast.error("That option is out of stock");
      return;
    }
    // Validate against stock, factoring in what's already in the cart.
    if (selectedVariation && quantity > remainingAllowable) {
      const inStock = Number(selectedVariation.inventory);
      const msg =
        inCartQty > 0
          ? `Only ${inStock} in stock — you already have ${inCartQty} of "${selectedVariation.name}" in your cart.`
          : `Only ${inStock} of "${selectedVariation.name}" left in stock.`;
      toast.error(msg);
      return;
    }
    addItem(product, quantity, selectedVariation);
    const label = selectedVariation ? `${product.name} (${selectedVariation.name})` : product.name;
    toast.success(`Added ${quantity} × "${label}" to cart`, {
      action: { label: "View cart", onClick: () => openCart() },
    });
    // Reset qty picker after successful add
    setQuantity(1);
  };

  if (loading) {
    return (
      <div className="bg-demolition-black min-h-screen flex items-center justify-center">
        <p className="font-mono text-white/50 animate-pulse">Loading product...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="bg-demolition-black min-h-screen flex flex-col items-center justify-center text-center px-6">
        <AlertTriangle className="w-12 h-12 text-demolition-red mb-3" />
        <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
          Product not found
        </h1>
        <p className="font-mono text-white/60 mt-2">
          The item you&apos;re looking for has been moved or sold out.
        </p>
        <button
          type="button"
          onClick={() => navigate("/shop")}
          className="mt-6 inline-flex items-center gap-2 px-5 py-2 border-2 border-demolition-red
            text-demolition-red hover:bg-demolition-red hover:text-white
            font-russo uppercase tracking-widest transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </button>
      </div>
    );
  }

  if (!product) return null;

  // A product is direct-sale if it has a price (or a variation with price). All
  // direct-sale items must have at least one active variation — otherwise the
  // item is staged / not-yet-launched and cannot be purchased.
  const isDirectSale = product.price != null || variations.some((v) => v.price != null);
  const isAffiliateOnly = !isDirectSale;
  const noVariationsAvailable = isDirectSale && variations.length === 0;
  const noStockAnywhere =
    isDirectSale && variations.length > 0 && variations.every((v) => Number(v.inventory) <= 0);

  return (
    <div className="bg-demolition-black min-h-screen py-10 px-6 md:px-12" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-teko text-base uppercase tracking-widest
            text-demolition-chrome hover:text-demolition-red transition-colors mb-6"
          data-testid="product-back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Gallery */}
          <section data-testid="product-gallery">
            <div className="aspect-square bg-demolition-subtle border border-white/10 overflow-hidden flex items-center justify-center">
              {images.length > 0 ? (
                <img
                  src={resolveImage(images[activeImageIdx])}
                  alt={`${product.name} ${activeImageIdx + 1}`}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <ShoppingBag className="w-24 h-24 text-demolition-red/40" />
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-4" data-testid="product-thumbnails">
                {images.map((src, idx) => (
                  <button
                    key={`${src}-${idx}`}
                    type="button"
                    onClick={() => setActiveImageIdx(idx)}
                    className={`aspect-square border overflow-hidden ${
                      activeImageIdx === idx
                        ? "border-demolition-red border-2"
                        : "border-white/10 hover:border-demolition-red/60"
                    }`}
                    aria-label={`View image ${idx + 1}`}
                    data-testid={`product-thumb-${idx}`}
                  >
                    <img
                      src={resolveImage(src)}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Info */}
          <section>
            <h1
              className="font-russo text-4xl md:text-5xl text-white uppercase tracking-tighter break-words"
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {!isAffiliateOnly && !noVariationsAvailable && (
              <p
                className="font-russo text-demolition-red text-4xl mt-4"
                data-testid="product-price"
              >
                {fmt(effectivePrice)}
              </p>
            )}

            {noVariationsAvailable && (
              <p
                className="font-teko text-2xl text-yellow-300 uppercase tracking-widest mt-4 border border-yellow-500/40 bg-yellow-500/10 px-4 py-3 inline-block"
                data-testid="product-unavailable"
              >
                Not currently available
              </p>
            )}

            {product.description && (
              <p
                className="font-mono text-white/70 text-base mt-6 whitespace-pre-line"
                data-testid="product-description"
              >
                {product.description}
              </p>
            )}

            {/* Variation selector */}
            {variations.length > 0 && (
              <div className="mt-8" data-testid="product-variations">
                <h2 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest mb-3">
                  Choose an option
                </h2>
                <ul className="space-y-2">
                  {variations.map((v) => {
                    const stock = stockState(v.inventory, product.low_stock_threshold);
                    const isSelected = selectedVariationId === v.id;
                    const disabled = stock.kind === "out";
                    return (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => !disabled && setSelectedVariationId(v.id)}
                          disabled={disabled}
                          className={`w-full flex items-center justify-between gap-3 px-4 py-3 border-2 transition-colors text-left
                            ${isSelected
                              ? "border-demolition-red bg-demolition-red/10"
                              : "border-white/15 hover:border-demolition-red/60"
                            }
                            ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
                          `}
                          data-testid={`product-variation-${v.id}`}
                          aria-pressed={isSelected}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {isSelected ? (
                              <Check className="w-4 h-4 text-demolition-red flex-shrink-0" />
                            ) : (
                              <span className="w-4 h-4 border border-white/30 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <p className="font-russo text-base text-white uppercase tracking-tight truncate">
                                {v.name}
                              </p>
                              {v.sku && (
                                <p className="font-mono text-xs text-white/40 truncate">SKU {v.sku}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {v.price != null && Number(v.price) !== Number(product.price) && (
                              <p className="font-russo text-base text-white">{fmt(v.price)}</p>
                            )}
                            <StockBadge stock={stock} />
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Quantity picker (only for direct-sale products with active variations + stock) */}
            {!isAffiliateOnly && !noVariationsAvailable && !noStockAnywhere && (
              <div className="mt-8 flex items-center gap-4" data-testid="product-qty-picker">
                <span className="font-teko text-lg text-demolition-chrome uppercase tracking-widest">
                  Qty
                </span>
                <div className="flex items-center border border-white/15">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1 || remainingAllowable <= 0}
                    className="px-3 py-2 text-white/80 hover:text-demolition-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Decrease quantity"
                    data-testid="product-qty-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={remainingAllowable || 1}
                    value={remainingAllowable <= 0 ? 0 : quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    disabled={remainingAllowable <= 0}
                    className="w-14 px-2 py-2 bg-transparent text-white font-mono text-center border-x border-white/15 focus:outline-none focus:text-demolition-red disabled:opacity-40"
                    aria-label="Quantity"
                    data-testid="product-qty-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= remainingAllowable || remainingAllowable <= 0}
                    className="px-3 py-2 text-white/80 hover:text-demolition-red disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Increase quantity"
                    data-testid="product-qty-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {selectedVariation && (
                  <span className="font-mono text-xs text-white/40" data-testid="product-qty-hint">
                    {remainingAllowable <= 0
                      ? inCartQty > 0
                        ? `Max in cart (${inCartQty})`
                        : "Out of stock"
                      : inCartQty > 0
                      ? `${remainingAllowable} more available (${inCartQty} in cart)`
                      : `${remainingAllowable} available`}
                  </span>
                )}
              </div>
            )}

            {/* Buy CTA */}
            <div className="mt-6">
              {isAffiliateOnly ? (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-demolition-red hover:bg-demolition-red/85
                    text-white font-russo uppercase tracking-widest text-lg px-8 py-3 border-2 border-demolition-red
                    transition-colors w-full md:w-auto"
                  data-testid="product-visit-link"
                >
                  <ExternalLink className="w-5 h-5" /> Visit Store
                </a>
              ) : noVariationsAvailable ? (
                <button
                  type="button"
                  disabled
                  className="inline-flex items-center justify-center gap-2 bg-demolition-red/30 cursor-not-allowed
                    text-white/70 font-russo uppercase tracking-widest text-lg px-8 py-3 border-2 border-demolition-red/40
                    w-full md:w-auto"
                  data-testid="product-unavailable-cta"
                >
                  Coming Soon
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={
                    noStockAnywhere ||
                    (selectedVariation && Number(selectedVariation.inventory) <= 0) ||
                    remainingAllowable <= 0
                  }
                  className="inline-flex items-center justify-center gap-2 bg-demolition-red hover:bg-demolition-red/85
                    disabled:bg-demolition-red/30 disabled:cursor-not-allowed
                    text-white font-russo uppercase tracking-widest text-lg px-8 py-3 border-2 border-demolition-red
                    transition-colors w-full md:w-auto"
                  data-testid="product-add-to-cart"
                >
                  <Plus className="w-5 h-5" />
                  {noStockAnywhere
                    ? "Sold Out"
                    : remainingAllowable <= 0 && inCartQty > 0
                    ? "Max Reached"
                    : "Add to Cart"}
                </button>
              )}
            </div>

            {/* Per-product stock summary for products WITHOUT variations */}
            {variations.length === 0 && !isAffiliateOnly && (
              <p className="font-mono text-xs text-white/40 mt-4">
                Ships from NJ · Sales tax calculated at checkout
              </p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function StockBadge({ stock }) {
  const cls = {
    in: "border-demolition-chrome/40 text-demolition-chrome",
    low: "border-yellow-500/50 text-yellow-300",
    out: "border-white/15 text-white/40",
  }[stock.kind];
  return (
    <p className={`font-mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 mt-1 inline-block ${cls}`}>
      {stock.label}
    </p>
  );
}
